import { Colors, Icon, Intent, Tooltip } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import * as React from 'react'
import config from '../../../../config'
import { formatDate } from '../../../../utils'
import { FulfillmentStatus } from '../../types'
import { dateInPast } from '../../utils'
import '../style.scss'

interface LockedUser {
  id: number
  username: string
}

export interface PickingAndPackStatusTagProps {
  pickAndPackStatus: string
  fulfillmentStatus: string
  lockedForPickingUntil?: Date
  lockedByUser?: LockedUser
  packingSlipPrintedAt?: Date
}

export const PickingAndPackStatusTag = ({
  pickAndPackStatus,
  fulfillmentStatus,
  lockedForPickingUntil,
  lockedByUser,
  packingSlipPrintedAt
}: PickingAndPackStatusTagProps) => {
  const calculateFulfillmentIntent = () => {
    if (fulfillmentStatus === FulfillmentStatus.FULFILLED) return Intent.PRIMARY
    if (fulfillmentStatus === FulfillmentStatus.UNFULFILLED) return Intent.WARNING

    return Intent.NONE
  }

  if (!config.PICK_LISTS()) {
    // Only render an icon for pack slip printed if on the legacy flow and a slip was printed.
    if (packingSlipPrintedAt) {
      return (
        <Tooltip content={<p>Packslip printed at {formatDate(packingSlipPrintedAt, true, false)}</p>}>
          <Icon icon={IconNames.SAVED} intent={calculateFulfillmentIntent()} />
        </Tooltip>
      )
    }

    return null
  }

  if (pickAndPackStatus === 'PACKED' || fulfillmentStatus === 'FULFILLED') {
    // This is the terminal pick/pack state.
    return (
      <Tooltip content="Order was packed for shipping.">
        <Icon color={Colors.GREEN3} size={18} icon={IconNames.TICK_CIRCLE} />
      </Tooltip>
    )
  }

  if (pickAndPackStatus === 'ITEMS_NOT_FOUND') {
    return (
      <Tooltip content="Some items were not found.">
        <Icon color={Colors.RED3} size={18} icon={IconNames.ISSUE} />
      </Tooltip>
    )
  }

  if (pickAndPackStatus === 'PICKED') {
    return (
      <Tooltip content="Order was picked.">
        <Icon className="pick-status-icon" intent={Intent.WARNING} size={10} icon={IconNames.FULL_CIRCLE} />
      </Tooltip>
    )
  }

  if (
    ['PICK_LIST_CREATED', 'PICK_NOT_STARTED'].includes(pickAndPackStatus) &&
    lockedForPickingUntil &&
    !dateInPast(lockedForPickingUntil)
  ) {
    // PICK_IN_PROGRESS if printed a pack slip with legacy flow OR the current pick list is locked
    // This was previously represented with a lock icon.
    return (
      <Tooltip
        content={
          lockedByUser ? `This order is being picked by ${lockedByUser.username}.` : 'This order is being picked.'
        }
      >
        <div className="pick-status-icon in-progress" />
      </Tooltip>
    )
  }

  return null
}
